/**
 * PremiumSidebar.jsx
 * Premium collapsible sidebar with grouped menu items
 *
 * Features:
 * - Collapsible groups with chevron animation
 * - localStorage persistence for expanded state
 * - Permission-based visibility
 * - Active route highlighting
 * - Premium glassmorphism styling
 * - Responsive (hidden on mobile, uses BottomMenu)
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';

// Icons
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MapIcon from '@mui/icons-material/Map';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BuildIcon from '@mui/icons-material/Build';
import EngineeringIcon from '@mui/icons-material/Engineering';
import TodayIcon from '@mui/icons-material/Today';
import SendIcon from '@mui/icons-material/Send';
import CalculateIcon from '@mui/icons-material/Calculate';
import LinkIcon from '@mui/icons-material/Link';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PlaceIcon from '@mui/icons-material/Place';
import DrawIcon from '@mui/icons-material/Draw';
import RouteIcon from '@mui/icons-material/Route';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import InsightsIcon from '@mui/icons-material/Insights';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import StarIcon from '@mui/icons-material/Star';
import TimelineIcon from '@mui/icons-material/Timeline';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import NotesIcon from '@mui/icons-material/Notes';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SpeedIcon from '@mui/icons-material/Speed';
import NatureIcon from '@mui/icons-material/Nature';
import HistoryIcon from '@mui/icons-material/History';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import PeopleIcon from '@mui/icons-material/People';
import DnsIcon from '@mui/icons-material/Dns';
import CampaignIcon from '@mui/icons-material/Campaign';
import TuneIcon from '@mui/icons-material/Tune';
import GridViewIcon from '@mui/icons-material/GridView';
import ExploreIcon from '@mui/icons-material/Explore';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LabelIcon from '@mui/icons-material/Label';
import PhonelinkSetupIcon from '@mui/icons-material/PhonelinkSetup';

import { usePremiumTokens } from '../theme/usePremiumTokens';
import { useTranslation } from './LocalizationProvider';
import {
 useAdministrator, useManager, useSuperAdmin, useRestriction 
} from '../util/permissions';
import useFeatures from '../util/useFeatures';

// Sidebar width constants
const SIDEBAR_WIDTH_EXPANDED = 260;
const SIDEBAR_WIDTH_COLLAPSED = 64;

// localStorage key for sidebar state
const STORAGE_KEY_COLLAPSED = 'premiumSidebar_collapsed';
const STORAGE_KEY_GROUPS = 'premiumSidebar_groups';

const PremiumSidebar = () => {
  const tokens = usePremiumTokens();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslation();

  // Safe token access with fallbacks (melhorado para visibilidade dark mode)
  const safeTokens = {
    surface: {
      base: tokens?.surface?.base || '#0d1117',
      glass: tokens?.surface?.glass || 'rgba(255, 255, 255, 0.08)',
      glassHover: tokens?.surface?.glassHover || 'rgba(255, 255, 255, 0.14)',
    },
    // Glassmorphism - alinhado com TopBar (blur 20px, opacidade 95%)
    glassmorphism: {
      heavy: {
        background: tokens?.glassmorphism?.heavy?.background || 'rgba(13, 17, 23, 0.95)',
        backdropFilter: 'blur(20px)',
      },
    },
    border: {
      subtle: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.08)',
    },
    text: {
      primary: tokens?.text?.primary || '#f1f5f9',
      secondary: tokens?.text?.secondary || '#94a3b8',
      muted: tokens?.text?.muted || '#64748b',
    },
    accent: {
      primary: tokens?.accent?.primary || '#00d4aa',
      subtle: tokens?.accent?.subtle || 'rgba(0, 212, 170, 0.12)',
    },
    radius: {
      md: tokens?.radius?.md || '8px',
    },
    transitions: {
      all: tokens?.transitions?.all || 'all 0.2s ease',
    },
  };

  // Permissions
  const admin = useAdministrator();
  const manager = useManager();
  const superAdmin = useSuperAdmin();
  const readonly = useRestriction('readonly');
  const disableReports = useRestriction('disableReports');
  const features = useFeatures();

  // User info
  const userId = useSelector((state) => state.session.user?.id);

  // Responsive
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Sidebar collapsed state
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY_COLLAPSED);
    return stored ? JSON.parse(stored) : false;
  });

  // Expanded groups state
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY_GROUPS);
    return stored ? JSON.parse(stored) : {
      monitoring: true,
      routing: false,
      reports: false,
      management: false,
      fleet: false,
      settings: false,
      admin: false,
    };
  });

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_COLLAPSED, JSON.stringify(collapsed));
  }, [collapsed]);

  // Persist expanded groups
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_GROUPS, JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  // Toggle group expansion
  const toggleGroup = (groupId) => {
    if (collapsed) {
      setCollapsed(false);
      setExpandedGroups((prev) => ({ ...prev, [groupId]: true }));
    } else {
      setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
    }
  };

  // Build link with device params
  const buildLink = (path) => {
    const sourceParams = new URLSearchParams(location.search);
    const deviceIds = sourceParams.getAll('deviceId');
    const groupIds = sourceParams.getAll('groupId');
    if (!deviceIds.length && !groupIds.length) {
      return path;
    }
    const params = new URLSearchParams();
    if (['/reports/chart', '/reports/route', '/replay'].includes(path)) {
      const [firstDeviceId] = deviceIds;
      if (firstDeviceId) params.append('deviceId', firstDeviceId);
    } else {
      deviceIds.forEach((id) => params.append('deviceId', id));
      groupIds.forEach((id) => params.append('groupId', id));
    }
    const search = params.toString();
    return search ? `${path}?${search}` : path;
  };

  // Check if route is active
  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  // Menu structure - 7 groups as per plan
  const menuGroups = useMemo(() => [
    // 1. Monitoring - Dashboards and visualization
    {
      id: 'monitoring',
      label: t('mapTitle'),
      icon: <MapIcon />,
      items: [
        { path: '/', label: t('mapTitle'), icon: <MapIcon />, exact: true },
        { path: '/overview', label: 'Overview', icon: <GridViewIcon /> },
        { path: '/fleet', label: t('fleetDashboard'), icon: <LocalShippingIcon /> },
        { path: '/temperature', label: t('temperatureMonitor'), icon: <ThermostatIcon /> },
      ],
    },
    // 2. Routing - Route planning and execution
    {
      id: 'routing',
      label: t('routePlanning'),
      icon: <AltRouteIcon />,
      hidden: readonly,
      items: [
        { path: '/settings/route-planning', label: t('routePlanning'), icon: <AltRouteIcon /> },
        { path: '/settings/route-execution', label: t('routeExecution') || 'Execução de Rotas', icon: <PlayCircleFilledIcon /> },
        { path: '/settings/operations-center', label: t('operationsCenter'), icon: <GridViewIcon /> },
        { path: '/settings/routing-dashboard', label: t('routingDashboard'), icon: <InsightsIcon /> },
        { path: '/settings/driver-activity', label: t('driverActivityDashboard'), icon: <LocalShippingIcon /> },
        { path: '/settings/driver-score', label: t('driverScoreDashboard'), icon: <SpeedIcon /> },
        { path: '/settings/routes', label: t('sharedRoutes'), icon: <LocalLibraryIcon /> },
      ],
    },
    // 3. Reports - All reporting features
    {
      id: 'reports',
      label: t('reportTitle'),
      icon: <DescriptionIcon />,
      hidden: disableReports,
      items: [
        { path: '/reports/combined', label: t('reportCombined'), icon: <StarIcon /> },
        { path: '/reports/events', label: t('reportEvents'), icon: <NotificationsActiveIcon /> },
        { path: '/replay', label: t('reportReplay'), icon: <RouteIcon /> },
        { path: '/heatmap', label: t('reportHeatmap'), icon: <WhatshotIcon /> },
        { path: '/eventmap', label: t('mapEventTitle'), icon: <ExploreIcon /> },
        { path: '/reports/trips', label: t('reportTrips'), icon: <PlayCircleFilledIcon /> },
        { path: '/reports/stops', label: t('reportStops'), icon: <PauseCircleFilledIcon /> },
        { path: '/reports/summary', label: t('reportSummary'), icon: <FormatListBulletedIcon /> },
        { path: '/reports/route', label: t('reportPositions'), icon: <TimelineIcon /> },
        { path: '/reports/chart', label: t('reportChart'), icon: <TrendingUpIcon /> },
        { path: '/reports/correlation', label: t('correlationTitle'), icon: <LinkIcon /> },
      ],
    },
    // 4. Management - Management reports
    {
      id: 'management',
      label: t('reportManagement'),
      icon: <AssessmentIcon />,
      hidden: disableReports,
      items: [
        { path: '/reports/management/speeding', label: t('reportSpeeding'), icon: <SpeedIcon /> },
        { path: '/reports/management/eco', label: t('reportEcoDriving'), icon: <NatureIcon /> },
        { path: '/reports/management/trips', label: t('reportTripsAnalysis'), icon: <RouteIcon /> },
        { path: '/reports/management/positions', label: t('reportPositionsHistory'), icon: <HistoryIcon /> },
        { path: '/reports/management/driver', label: t('reportDriverPerformance'), icon: <PersonIcon /> },
      ],
    },
    // 5. Fleet - Fleet management
    {
      id: 'fleet',
      label: t('deviceTitle'),
      icon: <DirectionsCarIcon />,
      hidden: readonly,
      items: [
        { path: '/settings/devices', label: t('deviceTitle'), icon: <DnsIcon /> },
        { path: '/settings/drivers', label: t('sharedDrivers'), icon: <PersonIcon />, hidden: features.disableDrivers },
        { path: '/settings/groups', label: t('settingsGroups'), icon: <FolderIcon />, hidden: features.disableGroups },
        { path: '/geofences', label: t('sharedGeofences'), icon: <DrawIcon /> },
        { path: '/settings/pois', label: t('poiTitle'), icon: <PlaceIcon /> },
        { path: '/settings/correlations', label: t('sharedCorrelations'), icon: <LinkIcon /> },
      ],
    },
    // 6. Settings - User settings
    {
      id: 'settings',
      label: t('settingsTitle'),
      icon: <SettingsIcon />,
      hidden: readonly,
      items: [
        { path: '/settings/preferences', label: t('sharedPreferences'), icon: <TuneIcon /> },
        { path: '/settings/dashboard', label: t('dashboardMenuText'), icon: <GridViewIcon /> },
        { path: `/settings/user/${userId}`, label: t('settingsUser'), icon: <PersonIcon /> },
        { path: '/settings/notifications', label: t('sharedNotifications'), icon: <NotificationsIcon /> },
        { path: '/settings/calendars', label: t('sharedCalendars'), icon: <TodayIcon />, hidden: features.disableCalendars },
        { path: '/settings/maintenances', label: t('sharedMaintenance'), icon: <BuildIcon />, hidden: features.disableMaintenance },
        { path: '/settings/maintenance-dashboard', label: t('maintenanceDashboard'), icon: <EngineeringIcon />, hidden: features.disableMaintenance },
        { path: '/settings/commands', label: t('sharedSavedCommands'), icon: <SendIcon />, hidden: features.disableSavedCommands },
        { path: '/settings/attributes', label: t('sharedComputedAttributes'), icon: <CalculateIcon />, hidden: features.disableComputedAttributes },
        { path: '/settings/costs', label: t('sharedCosts'), icon: <AttachMoneyIcon /> },
      ],
    },
    // 7. Admin - Administration
    {
      id: 'admin',
      label: t('userAdmin'),
      icon: <AdminPanelSettingsIcon />,
      hidden: !manager,
      items: [
        { path: '/settings/users', label: t('settingsUsers'), icon: <PeopleIcon /> },
        { path: '/settings/announcement', label: t('serverAnnouncement'), icon: <CampaignIcon /> },
        { path: '/settings/server', label: t('settingsServer'), icon: <SettingsIcon />, hidden: !admin },
        { path: '/settings/configurator', label: t('configuratorTitle'), icon: <SettingsRemoteIcon />, hidden: !admin },
        { path: '/reports/statistics', label: t('statisticsTitle'), icon: <BarChartIcon />, hidden: !admin },
        { path: '/reports/audit', label: t('reportAudit'), icon: <VerifiedUserIcon />, hidden: !admin },
        { path: '/reports/logs', label: t('sharedLogs'), icon: <NotesIcon /> },
        { path: '/reports/scheduled', label: t('reportScheduled'), icon: <EventRepeatIcon />, hidden: readonly },
        { path: '/settings/tags', label: t('tagsAdmin'), icon: <LabelIcon />, hidden: !superAdmin },
        { path: '/emulator', label: t('sharedEmulator'), icon: <PhonelinkSetupIcon />, hidden: !admin },
      ],
    },
  ], [t, admin, manager, superAdmin, readonly, disableReports, features, userId]);

  // Don't render on mobile
  if (isMobile) return null;

  return (
    <Box
      sx={{
        width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
        minWidth: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        // Glassmorphism alinhado com TopBar
        backgroundColor: safeTokens.glassmorphism.heavy.background,
        backdropFilter: safeTokens.glassmorphism.heavy.backdropFilter,
        WebkitBackdropFilter: safeTokens.glassmorphism.heavy.backdropFilter,
        borderRight: `1px solid ${safeTokens.border.subtle}`,
        transition: safeTokens.transitions.all,
        overflow: 'hidden',
      }}
    >
      {/* Collapse Toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-end',
          p: 1,
          borderBottom: `1px solid ${safeTokens.border.subtle}`,
          minHeight: 48,
        }}
      >
        <Tooltip title={collapsed ? t('sharedExpand') || 'Expandir' : t('sharedCollapse') || 'Recolher'} placement="right">
          <IconButton
            size="small"
            onClick={() => setCollapsed(!collapsed)}
            sx={{
              color: safeTokens.text.secondary,
              '&:hover': {
                backgroundColor: safeTokens.surface.glassHover,
                color: safeTokens.accent.primary,
              },
            }}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Menu Groups */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}>
        {menuGroups.filter((group) => !group.hidden).map((group) => {
          const visibleItems = group.items.filter((item) => !item.hidden);
          if (visibleItems.length === 0) return null;

          const isGroupActive = visibleItems.some((item) => isActive(item.path, item.exact));
          const isExpanded = expandedGroups[group.id];

          return (
            <Box key={group.id} sx={{ mb: 0.5 }}>
              {/* Group Header */}
              <ListItemButton
                onClick={() => toggleGroup(group.id)}
                sx={{
                  mx: collapsed ? 0.5 : 1,
                  px: collapsed ? 1.5 : 2,
                  py: 1,
                  borderRadius: safeTokens.radius.md,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  backgroundColor: isGroupActive && collapsed ? safeTokens.accent.subtle : 'transparent',
                  borderLeft: isGroupActive && !collapsed ? `3px solid ${safeTokens.accent.primary}` : '3px solid transparent',
                  '&:hover': {
                    backgroundColor: safeTokens.surface.glassHover,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed ? 0 : 36,
                    color: isGroupActive ? safeTokens.accent.primary : safeTokens.text.secondary,
                    justifyContent: 'center',
                  }}
                >
                  {group.icon}
                </ListItemIcon>
                {!collapsed && (
                  <>
                    <ListItemText
                      primary={group.label}
                      primaryTypographyProps={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        color: isGroupActive ? safeTokens.accent.primary : safeTokens.text.muted,
                      }}
                    />
                    {isExpanded ? (
                      <ExpandLessIcon sx={{ color: safeTokens.text.muted, fontSize: 18 }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: safeTokens.text.muted, fontSize: 18 }} />
                    )}
                  </>
                )}
              </ListItemButton>

              {/* Group Items */}
              <Collapse in={isExpanded && !collapsed} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {visibleItems.map((item) => {
                    const active = isActive(item.path, item.exact);
                    const link = item.path.startsWith('/reports/') ? buildLink(item.path) : item.path;

                    return (
                      <ListItemButton
                        key={item.path}
                        onClick={() => navigate(link)}
                        sx={{
                          mx: 1,
                          ml: 2,
                          px: 2,
                          py: 0.75,
                          borderRadius: safeTokens.radius.md,
                          backgroundColor: active ? safeTokens.accent.subtle : 'transparent',
                          borderLeft: active ? `3px solid ${safeTokens.accent.primary}` : '3px solid transparent',
                          color: active ? safeTokens.accent.primary : safeTokens.text.secondary,
                          '&:hover': {
                            backgroundColor: safeTokens.surface.glassHover,
                            color: safeTokens.text.primary,
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 32,
                            color: 'inherit',
                            '& .MuiSvgIcon-root': { fontSize: 18 },
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: '0.8125rem',
                            fontWeight: active ? 600 : 400,
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {/* Footer */}
      {!collapsed && (
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${safeTokens.border.subtle}`,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: safeTokens.text.muted,
              fontSize: '0.65rem',
              letterSpacing: '0.05em',
            }}
          >
            2ECOMP Fleet Premium
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export { SIDEBAR_WIDTH_EXPANDED, SIDEBAR_WIDTH_COLLAPSED };
export default PremiumSidebar;
